import { useSyncExternalStore } from "react";
import { isPremiumApp, WORKFLOW_PRODUCT_IDS } from "@shared/tiers";
import { paywallClosed, paywallOpened } from "@/lib/paywallEvents";
import { noteUserNotified } from "@/lib/downloadGate";

/**
 * A minimal, dependency-free global store (no new state library) so any
 * component -- a file tool's drop handler, a calculator's usage-logging
 * hook, anything -- can trigger the daily-limit paywall dialog without
 * needing to be nested under a specific React context provider. The
 * actual dialog is mounted once, globally, in Layout.tsx (same pattern
 * as useAutoResumeCheckout), and just reads this store.
 */
let isOpen = false;
let premiumTier = false;
let atDownload = false;
let atAction: PaywallMoment | null = null;
let blockedAction: PaywallMoment | null = null;
let blockedToolId: string | undefined;
const listeners = new Set<() => void>();

function notify() {
  Array.from(listeners).forEach((l) => l());
}

/** The action the paywall blocked. Its wording names the action: "copy" never says "download" (B-0156). */
export type PaywallMoment = "download" | "copy" | "generate" | "check" | "add files" | "format" | "convert" | "export" | "print" | "view";
/** Actions where the result already exists and is being taken away. */
const RESULT_MOMENTS: readonly PaywallMoment[] = ["download", "copy", "export", "view"];

const ACTION_GERUND: Record<PaywallMoment, string> = {
  download: "downloading",
  copy: "copying",
  generate: "generating",
  check: "checking",
  "add files": "adding files",
  format: "formatting",
  convert: "converting",
  export: "exporting",
  print: "printing",
  view: "viewing the model",
};

/** The blocked action as a gerund for a sentence: "copying needs a pass", never "download" for a copy. */
export function paywallActionNoun(moment: PaywallMoment): string {
  return ACTION_GERUND[moment];
}

/** The paywall heading. Copy and download are blocked here, so it never claims a result is ready (QA-397). */
export function paywallHeading(atResult: boolean): string {
  return atResult ? "Today's trial uses are done" : "You've used today's trial uses";
}

/**
 * Pass the tool's id so a Premium Application shows the Premium plans instead of the Professional ones.
 * `moment: "download"` is for the person whose result is ready but whose trial uses are gone: the dialog then
 * says the result is ready and what unlocks it, instead of the general "you've used today's trial".
 */
export function showDailyLimitPaywall(toolId?: string, moment?: PaywallMoment) {
  // Owner rule (31 Oct 2026): a Workflow Product has its own pricing, so the Day Pass / monthly plan offer never opens for it.
  if (toolId && (WORKFLOW_PRODUCT_IDS as readonly string[]).includes(toolId)) return;
  const wasOpen = isOpen;
  premiumTier = isPremiumApp(toolId);
  atDownload = !!moment && RESULT_MOMENTS.includes(moment);
  atAction = moment ?? null;
  // Remembered after the dialog closes, so the tool page can say why nothing happened (never a silent blank).
  blockedAction = moment ?? "generate";
  blockedToolId = toolId;
  isOpen = true;
  noteUserNotified();
  if (!wasOpen) paywallOpened(toolId, moment); // limit_hit + upgrade_cta_view, once per opening
  notify();
}

/** Test hook: forget what was blocked. */
export function clearBlockedAction() {
  blockedAction = null;
  blockedToolId = undefined;
  notify();
}

export function hideDailyLimitPaywall() {
  isOpen = false;
  paywallClosed();
  notify();
}

export function usePaywallDialogOpen() {
  return useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
    () => isOpen
  );
}

export function usePaywallIsPremium() {
  return useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
    () => premiumTier
  );
}

export function getPaywallAction(): PaywallMoment | null {
  return atAction;
}

export function getBlockedAction(toolId: string | undefined): PaywallMoment | null {
  return toolId && blockedToolId === toolId ? blockedAction : null;
}

/** The action the open paywall is about, or null for the general "you've used today's trial uses". */
export function usePaywallAction() {
  return useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
    () => atAction
  );
}

/** What was blocked on this tool today (set when the paywall opened for it), for the inline note under the title. */
export function useBlockedAction(toolId: string | undefined): PaywallMoment | null {
  return useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
    () => getBlockedAction(toolId)
  );
}

export function usePaywallAtDownload() {
  return useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
    () => atDownload
  );
}
